import type { AccessRoute } from '../../utils/pwa';
import type { ConnectionProbe, ProbeStageState } from '../../hooks/useServerReachability';

/** 内訳 1 行の状態。プローブ 1 段の実測結果をそのまま使う */
export type DiagnosticState = ProbeStageState;

/** 「どこまで届いているか」を 1 行で示す内訳 */
export interface ConnectionDiagnostic {
  /** 何を確認したか（プローブの段） */
  label: string;
  state: DiagnosticState;
  /** 結果の言い切り。記号だけでは伝わらないのでテキストでも持つ */
  detail: string;
}

export interface ConnectionGuidance {
  title: string;
  lead: string;
  /**
   * サーバに届いているのか届いていないのかの内訳。
   * 「接続できましたが応答しません」のような一文だけだと成否が読み取りづらいため、
   * 2 段プローブ（/healthz → /api/health）の**実測結果**をそのまま可視化する。
   * status から逆算してはいけない（同じ status が別々の段の失敗から生じるため）。
   */
  diagnostics: readonly ConnectionDiagnostic[];
  /** 上から順に試してもらう手順 */
  steps: readonly string[];
  /** 手順を試しても直らないときの補足 */
  note: string;
  /** バッジのアイコン種別。回線の問題かサーバ側の問題かを一目で分ける */
  icon: 'network' | 'server';
}

/** 案内文のうち、接続経路と起動方法で変わるテキスト部分 */
type GuidanceText = Pick<ConnectionGuidance, 'title' | 'lead' | 'steps' | 'note'>;

/**
 * 内訳のラベル。**利用者から見た主語**で書く。
 * 「アプリ」とだけ書くと手元の端末で開いている MedTube と読まれ、
 * サーバ側の障害なのに利用者が自分のアプリを再起動しに行ってしまう。
 */
const EDGE_LABEL = 'サーバーまで届いているか';
const API_LABEL = 'MedTube が動いているか';

/** 1 段目（/healthz）の結果表示 */
const EDGE_DETAIL: Record<DiagnosticState, string> = {
  ok: '届いています',
  ng: '届いていません',
  slow: '時間内に応答なし',
  unchecked: 'まだ確認していません',
  unknown: '判定できませんでした',
};

/** 2 段目（/api/health）の結果表示 */
const API_DETAIL: Record<DiagnosticState, string> = {
  ok: '動いています',
  ng: '応答がありません',
  slow: '応答が遅れています',
  unchecked: 'まだ確認できていません',
  unknown: '判定できませんでした',
};

/** 起動方法に応じた「それでも直らないとき」の一文 */
const RECOVERY_HINT = {
  pwa: 'それでも直らないときは、アプリを一度終了してから開き直してください。',
  browser: 'それでも直らないときは、ページを再読み込みしてください。',
} as const;

/** server-unreachable のときの案内を接続経路ごとに出し分ける */
const UNREACHABLE_GUIDANCE: Record<AccessRoute, GuidanceText> = {
  // 院外からの接続。Tailscale が落ちていればサーバには到達しようがない
  tailscale: {
    title: 'Tailscale に接続できていない可能性があります',
    lead: 'このアドレスは Tailscale 経由の接続です。Tailscale が動いていないとサーバーに届きません。',
    steps: [
      'Tailscale アプリを開く',
      'スイッチが「接続済み(Connected)」になっているか確認する',
      'オフになっていたら接続をオンにする',
      'この画面に戻って［再試行］を押す',
    ],
    note: 'Tailscale が接続済みでも直らない場合は、院内のサーバー本体が停止している可能性があります。管理者に連絡してください。',
  },
  // 院内から。ここで Tailscale の話をすると誤った復旧手順に時間を使わせてしまう
  local: {
    title: 'サーバーに接続できません',
    lead: '院内ネットワークへの接続を確認してください。',
    steps: [
      'Wi-Fi / 有線 LAN につながっているか確認する',
      '院内ネットワークに接続しているか確認する',
      '［再試行］を押す',
    ],
    note: 'ネットワークが正常でも直らない場合は、サーバー本体が停止している可能性があります。管理者に連絡してください。',
  },
  // 経路を判定できないケース。Tailscale は主文言ではなく補足に留める
  unknown: {
    title: 'サーバーに接続できません',
    lead: 'ネットワーク接続を確認してください。',
    steps: [
      'Wi-Fi / モバイル通信につながっているか確認する',
      '機内モードになっていないか確認する',
      '［再試行］を押す',
    ],
    note: '院外から接続している場合は、Tailscale アプリが「接続済み」になっているかも確認してください。',
  },
};

/**
 * 1 段目が時間内に返らなかったケース。
 * ここで Tailscale を疑わせると、**接続済みの VPN を切らせて**しまい、
 * 切った瞬間に本当に到達不能になる（案内が自己成就する）。設定は触らせない。
 */
const NETWORK_SLOW_GUIDANCE: GuidanceText = {
  title: '接続が非常に遅くなっています',
  lead: 'サーバーへの応答が時間内に返りませんでした。回線が混雑している可能性があります。',
  steps: ['電波状況の良い場所に移動する', '少し待ってから［再試行］を押す'],
  note: 'つながってはいるので、Tailscale や Wi-Fi の設定を切り替える必要はありません。',
};

/**
 * サーバまでは届いたが、2 段目が時間内に返らなかったケース。
 * 回線が生きていることは実測できているので、**回線側の対処を案内してはいけない**
 * （院内の有線接続の利用者を「電波の良い場所」へ歩かせても、原因はサーバ側なので直らない）。
 */
const API_SLOW_GUIDANCE: GuidanceText = {
  title: 'サーバーの応答が遅くなっています',
  lead: 'サーバーには届いていますが、MedTube からの応答が時間内に返りませんでした。',
  steps: ['少し待ってから［再試行］を押す', '解消しない場合は管理者に連絡する'],
  note: 'サーバーまでは届いているため、Tailscale や Wi-Fi の設定を変える必要はありません。',
};

/** サーバには届いているがアプリが応答しないケース。ネットワークの話は一切しない */
const API_UNAVAILABLE_GUIDANCE: GuidanceText = {
  title: 'サーバーで問題が発生しています',
  lead: 'サーバーには接続できましたが、サーバー上の MedTube が応答していません。',
  steps: ['しばらく待ってから［再試行］を押す', '解消しない場合は管理者に連絡する'],
  note: 'ネットワーク側の問題ではないため、Tailscale や Wi-Fi の設定を変える必要はありません。',
};

/** オーバーレイを出す＝何らかの失敗が確定している状態 */
type FailureStatus = Exclude<ConnectionProbe['status'], 'checking' | 'online'>;

const isFailureStatus = (status: ConnectionProbe['status']): status is FailureStatus =>
  status !== 'checking' && status !== 'online';

/** 段ごとの実測結果から案内文を選ぶ。同じ status でも落ちた段が違えば手順が変わる */
const resolveGuidanceText = (
  status: FailureStatus,
  probe: ConnectionProbe,
  route: AccessRoute
): GuidanceText => {
  if (status === 'server-unreachable') return UNREACHABLE_GUIDANCE[route];
  if (status === 'api-unavailable') return API_UNAVAILABLE_GUIDANCE;
  return probe.edge === 'ok' ? API_SLOW_GUIDANCE : NETWORK_SLOW_GUIDANCE;
};

/** 回線を疑わせるか、サーバを疑わせるか。案内文と図の指す原因を一致させる */
const resolveIcon = (status: FailureStatus, probe: ConnectionProbe): ConnectionGuidance['icon'] =>
  status === 'api-unavailable' || (status === 'network-slow' && probe.edge === 'ok')
    ? 'server'
    : 'network';

/**
 * 接続エラーの案内文を組み立てる。
 *
 * 正常時（'online'）とプローブ未完了時（'checking'）は null を返し、
 * 呼び出し側はオーバーレイを出さない。
 *
 * @param probe 到達性プローブの結果（status と段ごとの実測）
 * @param route 接続経路（classifyAccessRoute の結果）
 * @param isPwa ホーム画面から standalone 起動しているか。復旧手順の一文だけ変える
 */
export const buildConnectionGuidance = (
  probe: ConnectionProbe,
  route: AccessRoute,
  isPwa: boolean
): ConnectionGuidance | null => {
  const { status } = probe;
  if (!isFailureStatus(status)) return null;

  const text = resolveGuidanceText(status, probe, route);
  const recoveryHint = isPwa ? RECOVERY_HINT.pwa : RECOVERY_HINT.browser;

  return {
    ...text,
    note: `${text.note}${recoveryHint}`,
    diagnostics: [
      { label: EDGE_LABEL, state: probe.edge, detail: EDGE_DETAIL[probe.edge] },
      { label: API_LABEL, state: probe.api, detail: API_DETAIL[probe.api] },
    ],
    icon: resolveIcon(status, probe),
  };
};
